/**
 * Componente de linha para exibição de Template WhatsApp em formato lista
 * Exibe informações do template de forma compacta horizontal
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import {
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  FileText,
  Globe,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import VisibilityBadge from '@/components/shared/VisibilityBadge';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';

// Interface do template
interface WhatsAppTemplate {
  id: string;
  inbox_id: string;
  template_id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  rejection_reason: string | null;
  components: any;
  variables: string[] | null;
  example_values: Record<string, string> | null;
  provider_metadata: any;
  created_at: string;
  submitted_at: string | null;
  approved_at: string | null;
  classification_id: string | null;
  inbox?: {
    name: string;
    identifier: string;
  };
  classification?: {
    id: string;
    name: string;
    color: string;
  } | null;
  visibility?: EntityVisibility | null;
}

// Configuração de status
interface StatusConfig {
  label: string;
  icon: React.ElementType;
  color: string;
}

// Configuração de categoria
interface CategoryConfig {
  label: string;
  color: string;
}

interface TemplateListRowProps {
  template: WhatsAppTemplate;
  providerName?: string; // Nome do provider para exibição
  statusConfig: Record<string, StatusConfig>;
  categoryConfig: Record<string, CategoryConfig>;
  onEdit: (template: WhatsAppTemplate) => void;
  onPreview: (template: WhatsAppTemplate) => void;
  onDelete: (template: WhatsAppTemplate) => void;
  onManageVisibility?: (template: WhatsAppTemplate) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

/**
 * Extrai texto do body do template para preview
 */
const getBodyPreview = (components: any): string => {
  if (!components || !Array.isArray(components)) return '';
  
  const bodyComponent = components.find((c: any) => c.type === 'BODY');
  if (!bodyComponent?.text) return '';
  
  const text = bodyComponent.text;
  return text.length > 50 ? `${text.substring(0, 50)}...` : text;
};

/**
 * Conta variáveis no template
 */
const countVariables = (components: any): number => {
  if (!components || !Array.isArray(components)) return 0;
  
  let count = 0;
  components.forEach((c: any) => {
    if (c.text) {
      const matches = c.text.match(/\{\{\d+\}\}/g);
      if (matches) count += matches.length;
    }
  });
  return count;
};

/**
 * Formata data para exibição compacta
 */
const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
};

export function TemplateListRow({
  template,
  providerName,
  statusConfig,
  categoryConfig,
  onEdit,
  onPreview,
  onDelete,
  onManageVisibility,
  canEdit = true,
  canDelete = true,
}: TemplateListRowProps) {
  const status = statusConfig[template.status] || statusConfig.PENDING;
  const category = categoryConfig[template.category] || categoryConfig.UTILITY;
  const StatusIcon = status.icon;
  const bodyPreview = getBodyPreview(template.components);
  const variableCount = countVariables(template.components);

  return (
    <div className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors group">
      {/* Ícone */}
      <div className="p-2 rounded-lg bg-primary/10 shrink-0">
        <FileText className="h-4 w-4 text-primary" />
      </div>

      {/* Nome e Categoria */}
      <div className="min-w-[180px] max-w-[200px]">
        <p className="font-medium text-sm truncate" title={template.name}>
          {template.name}
        </p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <Badge variant="outline" className={cn("text-[10px] py-0", category.color)}>
            {category.label}
          </Badge>
          {/* Badge de classificação gerenciável */}
          {template.classification && (
            <Badge 
              variant="outline" 
              className="text-[10px] py-0"
              style={{ 
                borderColor: template.classification.color,
                color: template.classification.color 
              }}
            >
              {template.classification.name}
            </Badge>
          )}
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Globe className="h-3 w-3" />
            {template.language}
          </span>
        </div>
      </div>

      {/* Preview do texto */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-muted-foreground truncate">
          {bodyPreview || <span className="italic">Sem conteúdo de texto</span>}
        </p>
      </div>

      {/* Variáveis */}
      <div className="w-[80px] shrink-0 hidden sm:block">
        {variableCount > 0 && (
          <span className="px-2 py-0.5 rounded bg-muted text-xs font-mono">
            {variableCount} var
          </span>
        )}
      </div>

      {/* Inbox */}
      <div className="w-[120px] shrink-0 hidden md:block">
        {template.inbox && (
          <span className="text-xs text-muted-foreground truncate block" title={template.inbox.name}>
            {template.inbox.name}
          </span>
        )}
      </div>

      {/* Provider */}
      <div className="w-[100px] shrink-0 hidden lg:block">
        {providerName && (
          <Badge variant="secondary" className="text-[10px]">
            {providerName}
          </Badge>
        )}
      </div>

      {/* Data */}
      <div className="w-[70px] shrink-0 hidden lg:block">
        <span className="text-xs text-muted-foreground">
          {formatDate(template.created_at)}
        </span>
      </div>

      {/* Status */}
      <div className="w-[100px] shrink-0">
        <Badge variant="outline" className={cn("text-[10px]", status.color)}>
          <StatusIcon className="h-3 w-3 mr-1" />
          {status.label}
        </Badge>
      </div>

      {/* Visibilidade */}
      <div className="shrink-0 hidden md:block">
        <VisibilityBadge visibility={template.visibility ?? 'public'} />
      </div>



      {/* Ações */}
      <div className="shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onPreview(template)}>
              <Eye className="h-4 w-4 mr-2" />
              Visualizar
            </DropdownMenuItem>
            {canEdit && (
              <DropdownMenuItem onClick={() => onEdit(template)}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
            )}
            {onManageVisibility && (
              <DropdownMenuItem onClick={() => onManageVisibility(template)}>
                <ShieldCheck className="h-4 w-4 mr-2" />
                Visibilidade
              </DropdownMenuItem>
            )}
            {canDelete && (
              <DropdownMenuItem 
                onClick={() => onDelete(template)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
